import { addNewOrgPageData } from 'constants/data';

import EnrollmentPageWrapper from 'components/helperComponents/enrollmentPageWrapper/enrollmentPageWrapper';
import PageWrapper from 'components/helperComponents/pageWrapper/pageWrapper';
import NotePopup from 'modules/notes/notePopup';
import InviteExpiredPage from 'modules/oauth/invite/inviteExpired/inviteExpired';
import { Profile } from 'pages/clients/clientForm/profile';
import { Suspense, lazy } from 'react';
import 'react-big-calendar/lib/sass/styles.scss';
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useAutoLogout } from 'utils/hooks/useAutoLogout';
import { useReloadCacheLogout } from 'utils/useReloadCacheLogout';

// eslint-disable-next-line import/order
import { PERMISSION } from './utils/protectedRoutes/userRolesAndPermissionTypes';
// eslint-disable-next-line import/order
import PrivateRoutes from './utils/protectedRoutes';

import 'material-icons';
import 'react-toastify/dist/ReactToastify.css';

import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@mui/material';
import { LoadingSpinner } from 'components/loadingSpinner';
// eslint-disable-next-line import/order
import { LoginEmail } from 'components/loginEmail';

const Auth0Error = lazy(() => import('modules/oauth/errorPage/Auth0Error'));
const Auth0Permissions = lazy(() => import('modules/oauth/Auth0Permissions'));
const InvitePage = lazy(() => import('modules/oauth/invite/invitePage'));
const InvitePageStep2 = lazy(() => import('modules/oauth/invite/invitePageStep2'));
const RedirectToAuth0Login = lazy(() => import('modules/oauth/RedirectToAuth0Login'));
const ClientsPage = lazy(() => import('./pages/clients'));
const PerformancePage = lazy(() => import('./pages/performance'));
const DebugModal = lazy(() => import('./components/debugModal/debugModal'));

import { FeAppMonitoring } from 'modules/monitoring';
// eslint-disable-next-line import/order
import { OcrScannerAudio } from 'components/ocrAudio';
import OcrRecorderAudioFixedButton from 'components/ocrAudio/OcrRecorderAudioFixedButton';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MyTodos } from 'modules/myTodos';
import { AiScribe } from 'modules/aiScribe';
import useAccordionStateSync from 'utils/hooks/useAccordionState';
import { useIsOnlyVisibleToUserWithPermissions } from 'utils/hooks/useIsOnlyVisibleTo';
import useLaunchDarklyContextUpdate from 'utils/hooks/useLaunchDarklyContextUpdate';
import './styles/global.scss';
import { useResponsive } from 'utils/hooks/useMediaQuery';
import MobileClientsPage from 'pages/clients/mobileClients';
import { MobileClientForm } from 'pages/clients/mobileClientForm';
import { useAppSelector } from 'store/hooks';

const Page404 = lazy(() => import('./components').then((module) => ({ default: module.Page404 })));
const HomeProxyPage = lazy(() =>
  import('./pages').then((module) => ({ default: module.HomeProxyPage }))
);

const OrganizationsPage = lazy(() =>
  import('./pages').then((module) => ({ default: module.OrganizationsPage }))
);
const EnrollmentPlannerPage = lazy(() =>
  import('./pages').then((module) => ({ default: module.EnrollmentPlannerPage }))
);
const AboutPage = lazy(() => import('./pages/organizations/about/about'));
const ProgramSetupPage = lazy(
  () => import('./pages/organizations/programs/newProgram/programSetup')
);
const ProgramsPage = lazy(() => import('./pages/organizations/programs/programs'));
const UsersPage = lazy(() => import('./pages/organizations/users/users'));
const ImportSFTPPage = lazy(() => import('./pages/organizations/importSFTP/importSFTP'));
const IntegrationsPageEMR = lazy(() => import('./pages/organizations/integrations'));
const ActivatePage = lazy(() => import('./pages/organizations/activate/activate'));
const ClientForm = lazy(() => import('./pages/clients/clientForm'));
const OurWork = lazy(() => import('./pages/clients/clientForm/ourWork/ourWork'));
const AiScribePage = lazy(() => import('./pages/organizations/aiScribe/aiScribe'));
const MedicalPage = lazy(() => import('./pages/organizations/medical'));
const IdPPage = lazy(() => import('./pages/organizations/idp/idp'));
const BillingPage = lazy(() => import('./pages/organizations/billing/billing'));
const SitesPage = lazy(() => import('./pages/organizations/sites/sites'));

function App() {
  const { isTablet } = useResponsive();
  const { enableAudioAssist, aiScribe, enableBillingUpdates } = useFlags();
  useAccordionStateSync();
  useReloadCacheLogout();
  useLaunchDarklyContextUpdate();
  const { isLoading, isAuthenticated } = useAuth0();
  const canSeeMyTodos = useIsOnlyVisibleToUserWithPermissions([PERMISSION.VIEW_MY_TODOS]);
  const [searchParams] = useSearchParams();
  const { status, isPaused } = useAppSelector((s) => s.clients.aiScribe);
  const context = searchParams.get('context');

  useAutoLogout(15, status === 'recording' && !isPaused && aiScribe && context !== 'popup');

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center' }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <div>
      <FeAppMonitoring />
      <ToastContainer />
      {aiScribe && isAuthenticated && isTablet && context !== 'popup' && <AiScribe />}
      {enableAudioAssist && false && <OcrRecorderAudioFixedButton />}
      {isAuthenticated && canSeeMyTodos && context !== 'popup' && <MyTodos />}
      {process.env.REACT_APP_ENVIRONMENT !== 'prod' &&
        process.env.REACT_APP_ENVIRONMENT !== 'uat' && <DebugModal />}
      <Suspense fallback={null}>
        <Routes>
          {/* PUBLIC ROUTES */}
          {enableAudioAssist && <Route path="/audio" element={<OcrScannerAudio />} />}
          <Route path="*" element={<Page404 />} />
          <Route path="/" element={<HomeProxyPage />} />

          {/* POC auth0 */}
          {/* --------------- */}
          <Route path="/auth/login" element={<RedirectToAuth0Login />} />
          <Route path="/auth0" element={<LoginEmail />} />
          <Route path="/auth0/permissions" element={<Auth0Permissions />} />
          <Route path="/auth0Error" element={<Auth0Error />} />
          <Route path="invite/:login_hint?/:inviteCode?/:connection?" element={<InvitePage />} />
          <Route path="inviteStep2/" element={<InvitePageStep2 />} />

          {/* POC auth0 */}

          <Route path="/inviteExpired" element={<InviteExpiredPage />} />

          {/* PROTECTED ROUTES */}
          {
            <Route
              path="enrollmentPlanner"
              element={<PrivateRoutes allowedPermissions={[PERMISSION.ENROLL_READ]} />}
            >
              <Route
                path=""
                element={<EnrollmentPageWrapper pageContent={<EnrollmentPlannerPage />} />}
              />
            </Route>
          }
          <Route
            path="organizations"
            element={<PrivateRoutes allowedPermissions={[PERMISSION.ORG_READ]} />}
          >
            <Route
              path=""
              element={
                <PageWrapper hasSideNavigation={false} pageContent={<OrganizationsPage />} />
              }
            />
            <Route
              path="about/:organizationID?/:editMode?"
              element={
                <PageWrapper subHeaderData={addNewOrgPageData} pageContent={<AboutPage />} />
              }
            />
            <Route
              path="programs/:organizationID/:editMode?"
              element={
                <PageWrapper subHeaderData={addNewOrgPageData} pageContent={<ProgramsPage />} />
              }
            />
            <Route
              path="medical/:organizationID?/:editMode?"
              element={
                <PageWrapper subHeaderData={addNewOrgPageData} pageContent={<MedicalPage />} />
              }
            />
            <Route
              path="idp/:organizationID?/:editMode?"
              element={<PageWrapper subHeaderData={addNewOrgPageData} pageContent={<IdPPage />} />}
            />
            {enableBillingUpdates && (
              <Route
                path="sites/:organizationID?/:editMode?"
                element={
                  <PageWrapper subHeaderData={addNewOrgPageData} pageContent={<SitesPage />} />
                }
              />
            )}
            <Route
              path="users/:organizationID?/:editMode?"
              element={
                <PageWrapper subHeaderData={addNewOrgPageData} pageContent={<UsersPage />} />
              }
            />
            <Route
              path="import/:organizationID?/:editMode?"
              element={
                <PageWrapper subHeaderData={addNewOrgPageData} pageContent={<ImportSFTPPage />} />
              }
            />
            <Route
              path="integrations/:organizationID?/:editMode?"
              element={
                <PageWrapper
                  subHeaderData={addNewOrgPageData}
                  pageContent={<IntegrationsPageEMR />}
                />
              }
            />
            <Route
              path="ai-scribe/:organizationID?/:editMode?"
              element={
                <PageWrapper subHeaderData={addNewOrgPageData} pageContent={<AiScribePage />} />
              }
            />
            {enableBillingUpdates && (
              <Route
                path="billing/:organizationID?/:editMode?"
                element={
                  <PageWrapper subHeaderData={addNewOrgPageData} pageContent={<BillingPage />} />
                }
              />
            )}
            <Route
              path="activate/:organizationID?/:editMode?"
              element={
                <PageWrapper subHeaderData={addNewOrgPageData} pageContent={<ActivatePage />} />
              }
            />
            <Route
              path="programs/new-program/:organizationID/:editMode?"
              element={
                <PageWrapper subHeaderData={addNewOrgPageData} pageContent={<ProgramSetupPage />} />
              }
            />
            <Route
              path="programs/:organizationID/:programID/:editMode"
              element={
                <PageWrapper subHeaderData={addNewOrgPageData} pageContent={<ProgramSetupPage />} />
              }
            />
          </Route>
          <Route
            path="clients"
            element={<PrivateRoutes allowedPermissions={[PERMISSION.CLIENTS_READ]} />}
          >
            <Route
              path=""
              element={
                isTablet && aiScribe ? (
                  <MobileClientsPage />
                ) : (
                  <EnrollmentPageWrapper pageContent={<ClientsPage />} />
                )
              }
            />
            {(!isTablet || !aiScribe) && (
              <Route path="new" element={<EnrollmentPageWrapper pageContent={<ClientForm />} />}>
                <Route path="profile" element={<Profile />} />
                <Route path="" element={<Navigate to="profile" />} />
              </Route>
            )}
            {isTablet && aiScribe && (
              <Route
                path="new/profile"
                element={<EnrollmentPageWrapper pageContent={<MobileClientForm />} />}
              />
            )}
            <Route
              path="edit/:clientId"
              element={<EnrollmentPageWrapper pageContent={<ClientForm />} />}
            >
              <Route path="profile" element={<Profile />} />
              <Route path="ourWork" element={<OurWork />} />
              <Route path="" element={<Navigate to="ourWork" replace />} />
            </Route>
          </Route>
          <Route
            path="clients"
            element={<PrivateRoutes allowedPermissions={[PERMISSION.CLIENTS_READ]} />}
          >
            <Route path="edit/:clientId">
              <Route path="notes/:noteId" element={<NotePopup />}></Route>
            </Route>
          </Route>
          <Route
            path="reporting"
            element={<PrivateRoutes allowedPermissions={[PERMISSION.CLIENTS_READ]} />}
          >
            <Route path="" element={<EnrollmentPageWrapper pageContent={<PerformancePage />} />} />
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
